<template>
  <AmLoading 
    v-if="loading"
    :isLoading="loading"
    class="mx-auto pb-10"
  />
  <div 
    v-else
    v-click-outside="handleClickOutside"
    :class="['about-app grid gap-6 md:gap-10', {
      'w-[60%]': !app && mq.current === 'desktop',
      'w-[80%]': !app && mq.current === 'tablet',
      'w-[100%]': !app && mq.current === 'mobile',
      'mt-4': !app
    }]">
    <AmToast
      v-if="showAlert"
      :message="alertMessage"
      :duration="3000"
      margin="mt-28 ml-4"
      position="top-right"
      closable
    />

    <div>
      <h4 class="font-medium mb-4">
        {{ $t('aboutApp.form.visibility.selection') }}
      </h4>

      <div class="grid grid-rows-2 md:grid-rows-1 md:grid-cols-2 gap-4">
        <div
          class="border border-purple-500 hover:border-purple-500 p-4 rounded-md cursor-pointer"
          @click="setIsPrivate(AppVisibility.PUBLIC)"
        >
          <label class="flex gap-4 items-center cursor-pointer">
            <input
              v-model="state.isPrivate"
              type="radio"
              :value="AppVisibility.PUBLIC"
              class="hidden peer"
            />
            <div
              class="w-5 h-5 border-2 border-gray-400 rounded-full flex items-center justify-center peer-checked:border-purple-500"
            >
              <div 
                :class="['w-2.5 h-2.5 border-purple-500 bg-purple-500 rounded-full', {
                  'opacity-100': state.isPrivate === AppVisibility.PUBLIC,
                  'opacity-0': state.isPrivate !== AppVisibility.PUBLIC
                }]"
              ></div>


            </div>
            <span class="font-medium">
              {{ $t('aboutApp.form.visibility.public.label') }}
            </span>
          </label>
          <p class="text-gray-600 mt-1 text-sm">
            {{ $t('aboutApp.form.visibility.public.description') }}
          </p>
        </div>

        <div
          class="border border-gray-300 hover:border-gray-500 p-4 rounded-md cursor-pointer"
          @click="setIsPrivate(AppVisibility.PRIVATE)"
        >
          <label class="flex gap-4 items-center cursor-pointer">
            <input
              v-model="state.isPrivate"
              type="radio"
              :value="AppVisibility.PRIVATE"
              class="hidden peer"
            />
            <div
              class="w-5 h-5 border-2 border-gray-400 rounded-full flex items-center justify-center peer-checked:border-purple-500"
            >
              <div 
                :class="['w-2.5 h-2.5 border-purple-500 bg-purple-500 rounded-full', {
                  'opacity-100': state.isPrivate === AppVisibility.PRIVATE,
                  'opacity-0': state.isPrivate !== AppVisibility.PRIVATE
                }]"
              ></div>

            </div>
            <span class="font-medium">
              {{ $t('aboutApp.form.visibility.private.label') }}
            </span>
          </label>
          <p class="text-gray-600 mt-1 text-sm">
            {{ $t('aboutApp.form.visibility.private.description') }}
          </p>
        </div>

        
      </div>
    </div>


    <div 
      v-if="state.isPrivate === AppVisibility.PUBLIC || state.isPrivate === AppVisibility.PRIVATE"
      :class="['about-app--first-row', 'grid gap-6 md:grid-flow-col md:grid-cols-2 md:gap-10']">
      <AmInput
        v-model="state.nameApp"
        :label="$t(`aboutApp.form.nameApp.label`)"
        :hintText="$t(`aboutApp.form.nameApp.hintText`)"
        :placeholder="$t(`aboutApp.form.nameApp.placeholder`)"
        size="fullWidth"
        :validations="rules.nameApp"
        feedbackType="error"
        dataTestName="name-app-input"
      />

      <AmInput
        v-model="state.supportEmail"
        :label="$t(`aboutApp.form.support.label`)"
        :placeholder="$t(`aboutApp.form.support.placeholder`)"
        size="fullWidth"
        :validations="rules.supportEmail"
        dataTestName="support-email-input"
        feedbackType="error"
      />
    </div>

    <AmTextArea
      v-if="state.isPrivate === AppVisibility.PUBLIC || state.isPrivate === AppVisibility.PRIVATE"
      v-model="state.applicationDescription"
      :label="$t(`aboutApp.form.applicationDescription.label`)"
      :hintText="$t(`aboutApp.form.applicationDescription.hintText`)"
      :placeholder="$t(`aboutApp.form.applicationDescription.placeholder`)"
      size="fullWidth"
      :validations="rules.applicationDescription"
      feedbackType="error"
      dataTestName="application-description-input"
    />

    <div
      v-if="state.isPrivate === AppVisibility.PUBLIC || state.isPrivate === AppVisibility.PRIVATE"
      class="grid gap-6 md:grid-flow-col md:grid-cols-2 md:gap-10">
      <AmDropdown
        v-model="state.billingModel"
        :label=" $t(`aboutApp.form.billingModel.label`)"
        :placeholder="$t(`aboutApp.form.billingModel.placeholder`)"
        :validations="rules.billingModel"
        feedbackType="error"
        absolute
        size="fullWidth"
        dataTestName="billing-model-input"
        :options="[
          {
            title: $t('aboutApp.form.billingModel.options.billingByAppmax'),
            value: 'appmax-billing',
          },
          {
            title: $t('aboutApp.form.billingModel.options.billingByExternalPlataform'),
            value: 'external-billing',
          }
        ]"
      />
      <AmInput
        v-if="state.billingModel === 'appmax-billing'"
        v-model="state.billing"
        :label="
          $t(`aboutApp.form.billingValue.label`)
        "
        :hintText="
          $t(`aboutApp.form.billingValue.fixValue`)
        "
        required
        size="fullWidth"
        :moneyConfig="{ decimal: ',', thousands: '.', prefix: 'R$ ', precision: 2, max: 9999  }"
        :placeholder="
          $t(`aboutApp.form.billingValue.placeholder`)
        "
        :validations="rules.billing"
        feedbackType="error"
        dataTestName="billing-value-input"
      />
    </div>

    <div 
      v-if="state.isPrivate === AppVisibility.PUBLIC || state.isPrivate === AppVisibility.PRIVATE"
      class="flex justify-end mb-10 gap-2"
    >
      <AmButton
        v-if="!isEdit"
        variant="maxPrimary"
        :label="!isEditMode ? $t('aboutApp.actions.advanced') : $t('aboutApp.actions.saveAndSendToAnalysis')"
        :size="mq.current === 'mobile' ? 'fullWidth' : 'xxlarge'"
        :disabled="!formValid"
        :class="[{ 'py-3': mq.current === 'mobile' }]"
        dataTestName="submit-about-app-button"
        @eventKeyAmButton="submitForm"
        @click="submitForm"
      />
    </div>
  </div>
</template>

<script setup>
import { inject, ref, computed, watch, onBeforeMount } from 'vue'
import { AmInput, AmTextArea, AmButton, AmDropdown, AmLoading, AmToast } from '@appmax_npm/ds-appmax-v3'
import { minLength, required, email, helpers, maxLength } from '@vuelidate/validators'
import _ from 'lodash'
import { useVuelidate } from '@vuelidate/core'
import { useI18n } from 'vue-i18n'
import { useAppsStore } from '@/stores/apps'
import { useRouter } from 'vue-router'
import { useHttpStore } from '@/stores/http'
import { useAppStore, ScreenMode } from '@/stores/modules/app'
import { useReturnBarStore } from '@/stores/returnBar'
import AppVisibility from '@/enums/AppVisibility'

const returnBarStore = useReturnBarStore()

const { app, appID, isEdit } = defineProps(['app', 'appID', 'isEdit'])
const emit = defineEmits(['formSubmitIsFinish'])
const mq = inject('mq')
const httpStore = useHttpStore()
const { t } = useI18n()
const appsStore = useAppsStore()
const router = useRouter()
const loading = ref(false)
const showAlert = ref(false)
const alertMessage = ref(null)
const backToAboutApp = ref(false)

const appStore = useAppStore()

const state = ref({
  nameApp: undefined,
  supportEmail: undefined,
  applicationDescription: undefined,
  billingModel: '',
  billing: '',
  isPrivate: null,
});

const isEditMode = computed(() => !!app)

const createAppRules = computed(() => ({
  nameApp: {
    required: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.requiredInput')}`,
      required
    ),
    minLength: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.minLength', {
        minlength: '2'
      })}`,
      minLength(2)
    ),
    maxLength: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.maxLength', {
        maxlength: '30'
      })}`,
      maxLength(30)
    )
  },
  billingModel: {
    required: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.requiredInput')}`,
      required
    ),
    minLength: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.minLength', {
        minlength: '2'
      })}`,
      minLength(2)
    )
  },
  applicationDescription: {
    required: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.requiredInput')}`,
      required
    ),
    minLength: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.minLength', {
        minlength: '5'
      })}`,
      minLength(5)
    ),
    maxLength: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.maxLength', {
        maxlength: '150'
      })}`,
      maxLength(150)
    )
  },
  billing: {
    minLength: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.minLength', {
        minlength: '5'
      })}`,
      minLength(5)
    )
  },
  supportEmail: {
    required: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.requiredInput')}`,
      required
    ),
    minLength: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.minLength', {
        minlength: '5'
      })}`,
      minLength(5)
    ),
    email: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.email')}`,
      email
    )
  }
}))

const editRules = computed(() => ({
  nameApp: {
    required: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.requiredInput')}`,
      required
    ),
    minLength: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.minLength', {
        minlength: '2'
      })}`,
      minLength(2)
    ),
    maxLength: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.maxLength', {
        maxlength: '30'
      })}`,
      maxLength(30)
    )
  },
  supportEmail: {
    required: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.requiredInput')}`,
      required
    ),
    minLength: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.minLength', {
        minlength: '5'
      })}`,
      minLength(5)
    ),
    email: helpers.withMessage(
      `${t('createAppForm.feedbackMessages.email')}`,
      email
    )
  },
  applicationDescription: {
    required: helpers.withMessage(
      `${t('aboutApp.feedbacks.rules.applicationDescription')}`,
      required
    )
  },
  billingModel: {
    required: helpers.withMessage(
      `${t('aboutApp.feedbacks.rules.billingModel')}`,
      required
    )
  },
}))

const setIsPrivate = (value) => {
  if (isEditMode.value) {
    return
  }

  state.value.isPrivate = value
}

const rules = app ? editRules.value : createAppRules.value

const v$ = useVuelidate(rules, state)
const formValid = ref(false)

const validForm = async () => {
  let validations = {}

  validations = {
    nameApp: await v$.value.nameApp.$validate(),
    applicationDescription: await v$.value.applicationDescription.$validate(),
    billingModel: await v$.value.billingModel.$validate(),
  }

  if (!app) {
    validations = {
      nameApp: await v$.value.nameApp.$validate(),
      applicationDescription: await v$.value.applicationDescription.$validate(),
      billingModel: await v$.value.billingModel.$validate(),
      supportEmail: await v$.value.supportEmail.$validate(),
    }

    if (state.value.billingModel === 'appmax-billing') {
      validations.billing = await v$.value.billing.$validate()
    }
  }

  return Object.values(validations).every(validation => validation)
}

watch(
  () => state,
  async () => {
    formValid.value = await validForm()
  },
  { deep: true }
)

onBeforeMount(async () => {
  backToAboutApp.value = window.history.state.parameter
  if (appStore.screenMode === ScreenMode.DRAFT || appStore.screenMode === ScreenMode.EDITING || backToAboutApp.value == true) {
    state.value.nameApp = appStore.app.nameApp
    state.value.supportEmail = appStore.app.supportEmail
    state.value.applicationDescription = appStore.app.applicationDescription
    state.value.billingModel = appStore.app.billingModel === '1' ? 'appmax-billing' : 'external-billing';
    state.value.billing = appStore.app.billingModel === '1' ? appStore.app.billing : '';
    state.value.isPrivate = appStore.app.isPrivate ? 2 : 1

    console.log(appsStore.app, state.value)
  }

  if (appStore.screenMode === ScreenMode.DRAFT || appStore.screenMode === ScreenMode.CREATION) {
    returnBarStore.title = t(`createAppForm.aboutApp.title`)
  }

});

function handleClickOutside() {
  saveAppOnStage()
  appStore.validateEditForm = formValid.value
  appStore.modificatedSteps = appStore.addStepModificated('aboutApp')
}

function saveAppOnStage() {
  const methods = {
    'appmax-billing': '1',
    'external-billing': '2'
  };

  appStore.app.nameApp = state.value.nameApp
  appStore.app.supportEmail = state.value.supportEmail
  appStore.app.applicationDescription = state.value.applicationDescription
  appStore.app.billingModel = methods[state.value.billingModel]
  appStore.app.isPrivate = state.value.isPrivate == 2 ? true : false

  if (state.value.billingModel === 'appmax-billing') {
    appStore.app.billing = _.replace(state.value.billing, /[^0-9]/g, '').replace(/(^|[^0-9])0+(?=[0-9])/g, '$1');
  }
}

function submitForm() {
  loading.value = true

  const methods = {
    'appmax-billing': '1',
    'external-billing': '2'
  };

  const data = {
    name: state.value.nameApp,
    app_description: state.value.applicationDescription,
    support_email: state.value.supportEmail,
    billing_model: methods[state.value.billingModel],
    is_private: appStore.app.isPrivate
  };

  if (state.value.billingModel === 'appmax-billing') {
    data.app_billing_rate = _.replace(state.value.billing, /[^0-9]/g, '').replace(/(^|[^0-9])0+(?=[0-9])/g, '$1');
  }

  if (!app && backToAboutApp.value != true) {
    httpStore.sendAboutApp(data).then((response) => {
      saveAppOnStage()
      appsStore.setAppID(response.data.data)
      router.push({ name: 'create-app_images-app' })
    }).catch((error) => {
      loading.value = false
      showAlert.value = true

      const limitReason = error?.response?.status === 403 ? error?.response?.data?.reason : null

      alertMessage.value = {
        alertType: 'warning',
        title: computed(() =>
          limitReason
            ? t('createAppView.limit.title')
            : t('aboutApp.feedbacks.form.error.title')
        ),
        description: computed(() =>
          limitReason
            ? t(`createAppView.limit.${limitReason}`)
            : t('aboutApp.feedbacks.form.error.description')
        )
      }
    })

    return
  }

  const appValueID = appID || appStore.app.id;

  httpStore.editAboutApp(appValueID, data).then(() => {
    saveAppOnStage()
    if (backToAboutApp.value) {
      router.push({ name: 'create-app_images-app', state: { parameter: true } })
    }
    emit('formSubmitIsFinish', false);
    loading.value = false
  }).catch(() => {
    emit('formSubmitIsFinish', false);
    loading.value = false

    alertMessage.value = {
      alertType: 'warning',
      title: computed(() => t('aboutApp.feedbacks.form.error.title')),
      description: computed(() =>
        t('aboutApp.feedbacks.form.error.description')
      )
    }
  })
}
</script>
